import { window } from "global";
import { useEffect, useMemo } from "react";
import { useGlobal } from "reactn";
import { useHistory, useLocation } from "react-router-dom";
import { useQueryString } from "@ugg/shared/hooks/use-query-string";
import { REGION_OPTIONS } from "./leaderboard-filters";
import { buildQueryParams } from "@ugg/shared/utils/url-query-params";
import { Helmet } from "react-helmet-async";
import { MediaQuery } from "@outplayed/responsive";
import { LeaderboardError } from "./components/LeaderboardError";
import { LeaderboardLoading } from "./components/LeaderboardLoading";
import { LeaderboardHeader } from "./components/LeaderboardHeader";
import { LeaderboardRegionFilter } from "./components/filters/LeaderboardRegionFilter";
import { LeaderboardSummonerInput } from "./components/filters/LeaderboardSummonerInput";
import { LeaderboardTableContainer } from "./components/LeaderboardTableContainer";
import { ReactComponent as FilterIcon } from "@ugg/shared/assets/svg/filters-icon.svg";
import { useLeaderboardPage } from "@ugg/shared/api/requests/leaderboards/leaderboard-page";
import { usePlayerOverallRanking } from "@ugg/shared/api/requests/leaderboards/ladder-ranking";
import { splitRiotId } from "@ugg/shared/utils/riot-id-helpers";
import { QueueTypeN } from "@ugg/shared/utils/queue-type-helpers";

const FiltersRow = () => {
  const queryParams = useQueryString();
  const { region } = queryParams;

  return (
    <div className="flex items-center justify-between my-[24px]">
      <div className="flex items-center">
        <div className="flex items-center mr-[24px] max-sm:mr-[12px]">
          <FilterIcon className="flex-none w-[18px]" />
          <span className="ml-[10px] max-sm:hidden text-[14px] font-bold">Filters</span>
        </div>
        <MediaQuery min="TABLET" max="DESKTOP_LARGE">
          <LeaderboardRegionFilter value={region} />
        </MediaQuery>
        <MediaQuery min="MOBILE_SMALL" max="MOBILE_LARGE">
          <LeaderboardRegionFilter value={region} options={REGION_OPTIONS} isMobile />
        </MediaQuery>
      </div>
      <LeaderboardSummonerInput className="max-xs:hidden" />
    </div>
  );
};

export function LeaderboardRanking() {
  const history = useHistory();
  const location = useLocation();
  const [globalRegion] = useGlobal("clientRegion");
  const queryParams = useQueryString();
  const { region, user, page } = queryParams;
  const [userRiorUserName, userRiotTagLine] = splitRiotId(user);

  useEffect(() => {
    if (!region || !REGION_OPTIONS.find((option) => option.value === region.toLowerCase())) {
      history.replace({ pathname: location.pathname, search: `region=${globalRegion}` });
    }
  }, [region]);

  useEffect(() => {
    window && window.scrollTo(0, 0);
  }, [JSON.stringify(queryParams)]);

  const selectedRegion = useMemo(
    () => REGION_OPTIONS.find((option) => option.value === (region && region.toLowerCase())),
    [region],
  );
  const { fullLabel } = selectedRegion || {};

  const {
    loading: loadingOverallRanking,
    data: overallRankingData,
    error: errorOverallRanking,
  } = usePlayerOverallRanking(userRiorUserName, userRiotTagLine, region, QueueTypeN.RANKED_SOLO, {
    ssr: false,
    skip: !userRiorUserName || !userRiotTagLine,
  });

  const { overallRanking } = overallRankingData || {};
  const { overallRanking: overallRankingNum } = overallRanking || {};
  const summonerPageNum = overallRankingNum && Math.floor(overallRankingNum / 100) + (overallRankingNum % 100 === 0 ? 0 : 1);
  const leaderboardPageNum =
    summonerPageNum && !isNaN(summonerPageNum) ? summonerPageNum : !isNaN(parseInt(page)) ? parseInt(page) : 1;

  const {
    loading: loadingLeaderboardPage,
    data: leaderboardPageData,
    error: errorLeaderboardPage,
  } = useLeaderboardPage(region, QueueTypeN.RANKED_SOLO, leaderboardPageNum, {
    ssr: false,
    skip: loadingOverallRanking,
  });

  const { leaderboardPage } = leaderboardPageData || {};
  const { players } = leaderboardPage || {};
  const isLoading = loadingOverallRanking || loadingLeaderboardPage;
  let content = null;

  if (isLoading) {
    content = <LeaderboardLoading />;
  } else if (!players || players.length === 0) {
    content = <LeaderboardError resetLink={{ pathname: location.pathname, search: buildQueryParams({ region }) }} />;
  } else {
    content = <LeaderboardTableContainer page={leaderboardPageNum} data={leaderboardPageData?.leaderboardPage} />;
  }

  return (
    <div className="w-full">
      <Helmet>
        <title>LoL Leaderboards - Best Ranking Challenger Players All Regions</title>
        <meta
          name="description"
          content="U.GG's player leaderboards track the highest ranked League of Legends players across every region. Player profiles include best champions and LP per game."
        />
      </Helmet>
      <LeaderboardHeader mainTitle={"Leaderboards"} subTitle={fullLabel || ""} />
      <FiltersRow />
      {content}
    </div>
  );
}
